/**
 * `<profile-dialog>`: who you are at the table - a display name from the
 * saloon's own list, a preferred team color, and the four-color deck.
 *
 * A native modal <dialog> brings the focus trap, the backdrop and Escape with
 * it. On a first visit there is nothing to go back to, so it cannot be
 * dismissed, only completed.
 */

import type { TeamColor } from "../../server/protocol.js";
import { h } from "../dom.js";
import { emit } from "../events.js";
import { BaseElement } from "./base-element.js";
import { createColorSwatches } from "./color-swatches.js";
import styles from "./dialogs.css";

/** The return value a save closes the dialog with; anything else was a cancel. */
const SAVED = "saved";

export interface ProfileSeed {
    readonly name: string;
    readonly color: TeamColor;
    readonly fourColorDeck: boolean;
    /** False on a first visit, where there is nothing to cancel back to. */
    readonly dismissable: boolean;
    /** Why the server replaced the name asked for, if it did. */
    readonly notice: string | null;
}

export class ProfileDialog extends BaseElement {
    readonly #dialog = h("dialog", { "aria-labelledby": "profile-heading" });

    readonly #notice = h("p", { class: "notice", role: "status", hidden: true });

    readonly #name = h("p", { "class": "name", "aria-labelledby": "name-label" });

    readonly #swatches = createColorSwatches("team-color");

    readonly #deck = h("input", { type: "checkbox" });

    readonly #cancel = h("button", { type: "button", class: "quiet" }, "Cancel");

    #dismissable = false;

    constructor() {
        super(styles);

        const randomize = h("button", { type: "button", class: "quiet" }, "Generate another");
        const form = h(
            "form",
            { method: "dialog" },
            h("h2", { id: "profile-heading" }, "Who are you at the table?"),
            this.#notice,
            h(
                "div",
                { class: "field" },
                h("span", { class: "label", id: "name-label" }, "Display name"),
                this.#name,
                randomize,
                h("p", { class: "help" }, "Names come from the saloon's own list, so the server always agrees with your choice.")
            ),
            h(
                "fieldset",
                { class: "field" },
                h("legend", { class: "label" }, "Preferred team color"),
                h("p", { class: "help" }, "A preference, not a promise - a table may hand you a different one."),
                this.#swatches.element
            ),
            h(
                "label",
                { class: "check" },
                this.#deck,
                h(
                    "span",
                    {},
                    "Four-color deck",
                    h("span", { class: "help" }, "Clubs green and diamonds blue, so every suit differs by color as well as shape.")
                )
            ),
            h("div", { class: "actions" }, this.#cancel, h("button", { type: "submit", class: "primary" }, "Take a seat"))
        );

        randomize.addEventListener("click", () => emit(this, "names-requested", null));
        this.#cancel.addEventListener("click", () => this.#dialog.close());

        form.addEventListener("submit", (event) => {
            event.preventDefault();

            emit(this, "profile-save", {
                name: this.#name.textContent,
                color: this.#swatches.read(),
                fourColorDeck: this.#deck.checked
            });

            this.#dialog.close(SAVED);
        });

        this.#dialog.addEventListener("cancel", (event) => {
            if (!this.#dismissable) {
                event.preventDefault();
            }
        });

        // The close event arrives a task later, so whether it was a save is
        // read from the dialog's return value rather than from a flag.
        this.#dialog.addEventListener("close", () => {
            if (this.#dialog.returnValue !== SAVED) {
                emit(this, "profile-cancel", null);
            }
        });

        this.#dialog.append(form);
        this.root.append(this.#dialog);
    }

    /** The name on offer; updated in place as the server suggests new ones. */
    set name(value: string) {
        this.#name.textContent = value;
    }

    set notice(value: string | null) {
        this.#notice.hidden = value === null;
        this.#notice.textContent = value ?? "";
    }

    /**
     * Opens the dialog seeded from the stored profile. Seeding happens on each
     * open rather than once, so edits are discarded on cancel.
     */
    show(seed: ProfileSeed): void {
        this.#dismissable = seed.dismissable;
        this.#cancel.hidden = !seed.dismissable;
        this.name = seed.name;
        this.notice = seed.notice;
        this.#swatches.check(seed.color);
        this.#deck.checked = seed.fourColorDeck;

        if (!this.#dialog.open) {
            this.#dialog.returnValue = "";
            this.#dialog.showModal();
        }
    }

    protected render(): void {
        // Everything is built once and driven through show() and the setters.
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "profile-dialog": ProfileDialog;
    }
}

customElements.define("profile-dialog", ProfileDialog);
